'use client';
import { useRouter } from 'next/navigation';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import { BrowseFiltersList, ComponentErrorBoundary, SearchInput } from '@/components';
import { useT } from '@/hooks';
import { SearchFiltersList } from './SearchFiltersList';

import { BROWSE_FILTERS, BrowseFilter, EVENT_STATUS } from '@/constants';

interface SearchSidebarProps {
  searchQuery: string;
  status: string;
  searchInputRef: React.RefObject<HTMLDivElement>;
  onSearchChange: (value: string) => void;
  onStatusChange: (status: string) => void;
  onClearFilters: () => void;
}

export function SearchSidebar({
  searchQuery,
  status,
  searchInputRef,
  onSearchChange,
  onStatusChange,
  onClearFilters,
}: SearchSidebarProps) {
  const router = useRouter();
  const { t } = useT();

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
  };

  const handleBrowseClick = (filter: BrowseFilter) => {
    if (filter.path) {
      router.push(filter.path);
      return;
    }
  };

  return (
    <ComponentErrorBoundary componentName="Sidebar">
      <Box
        sx={{
          width: '100%',
          height: { xs: 'auto', lg: 'fit-content' },
          position: { xs: 'relative', lg: 'sticky' },
          top: { xs: 'auto', lg: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Main Sidebar Card */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            boxShadow: 1,
          }}
        >
          {/* Search Field */}
          <Stack direction="column" alignItems="center" sx={{ py: 3 }}>
            <SearchInput
              value={searchQuery}
              onChange={handleSearchChange}
              inputRef={searchInputRef}
              borderless
              placeholder={t('search.search')}
            />
          </Stack>

          <Divider />

          {/* Sort By Section */}
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.secondary',
                mb: 2,
                opacity: 0.8,
              }}
            >
              {t('search.browse')}
            </Typography>
            <BrowseFiltersList
              title={t('search.browse')}
              filters={BROWSE_FILTERS}
              onFilterClick={handleBrowseClick}
            />
          </Box>

          <Divider />

          {/* Search Filters Section */}
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.secondary',
                mb: 2,
                opacity: 0.8,
              }}
            >
              {t('search.filters')}
            </Typography>
            <SearchFiltersList
              filters={EVENT_STATUS}
              onFilterClick={onStatusChange}
              activeValue={status}
            />
          </Box>
        </Box>

        {/* Clear Filters - Centered below card */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={onClearFilters}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 'normal',
              opacity: 0.7,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'text.primary',
                opacity: 1,
                textDecoration: 'underline',
              },
            }}
          >
            {t('search.clearFilters')}
          </Button>
        </Box>
      </Box>
    </ComponentErrorBoundary>
  );
}
